"use client";

import {
	AlertCircle,
	CheckCircle,
	InfoCircle,
	XClose,
} from "@untitledui/icons";
import type { ReactNode } from "react";
import type {
	DialogProps as AriaDialogProps,
	ModalOverlayProps as AriaModalOverlayProps,
} from "react-aria-components";
import {
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { Button } from "@/shared/components/button/button-untitledui";
import { cx } from "@/utils/cx";

export const DialogUntitledUITrigger = AriaDialogTrigger;

export const ModalOverlay = (props: AriaModalOverlayProps) => (
	<AriaModalOverlay
		{...props}
		className={(state) =>
			cx(
				"fixed inset-0 z-50 flex min-h-dvh w-full items-end justify-center overflow-y-auto bg-overlay/70 px-4 pt-4 pb-[clamp(16px,8vh,64px)] outline-hidden backdrop-blur-[6px] sm:items-center sm:justify-center sm:p-8",
				!!state.isEntering && "fade-in animate-in duration-300 ease-out",
				!!state.isExiting && "fade-out animate-out duration-200 ease-in",
				typeof props.className === "function"
					? props.className(state)
					: props.className
			)
		}
	/>
);

export const Modal = (props: AriaModalOverlayProps) => (
	<AriaModal
		{...props}
		className={(state) =>
			cx(
				"max-h-full w-full align-middle outline-hidden max-sm:overflow-y-auto max-sm:rounded-xl",
				!!state.isEntering && "zoom-in-95 animate-in duration-300 ease-out",
				!!state.isExiting && "zoom-out-95 animate-out duration-200 ease-in",
				typeof props.className === "function"
					? props.className(state)
					: props.className
			)
		}
	/>
);

export const DialogUntitledUI = (props: AriaDialogProps) => (
	<AriaDialog
		{...props}
		className={cx(
			"flex w-full items-center justify-center outline-hidden",
			props.className
		)}
	/>
);

type ModalBackgroundCircleProps = {
	className?: string;
	variant?: "success" | "error" | "warning" | "info";
	customIcon?: ReactNode;
};

export const ModalBackgroundCircle = ({
	className,
	customIcon,
	variant = "success",
}: ModalBackgroundCircleProps) => (
	<div className={className}>
		<svg
			aria-hidden="true"
			className="-top-[120px] -left-[120px] absolute z-0"
			fill="none"
			height="336"
			viewBox="0 0 336 336"
			width="336"
			xmlns="http://www.w3.org/2000/svg"
		>
			<mask
				height="336"
				id="mask0_4947_375931"
				maskUnits="userSpaceOnUse"
				style={{ maskType: "alpha" }}
				width="336"
				x="0"
				y="0"
			>
				<rect fill="url(#paint0_radial_4947_375931)" height="336" width="336" />
			</mask>
			<g mask="url(#mask0_4947_375931)">
				<circle cx="168" cy="168" r="47.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="47.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="71.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="95.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="119.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="143.5" stroke="#E9EAEB" />
				<circle cx="168" cy="168" r="167.5" stroke="#E9EAEB" />
			</g>
			<defs>
				<radialGradient
					cx="0"
					cy="0"
					gradientTransform="translate(168 168) rotate(90) scale(168 168)"
					gradientUnits="userSpaceOnUse"
					id="paint0_radial_4947_375931"
					r="1"
				>
					<stop />
					<stop offset="1" stopOpacity="0" />
				</radialGradient>
			</defs>
		</svg>
		{customIcon ? customIcon : null}
		{!customIcon && variant === "success" && (
			<CheckCircle className="absolute top-6 left-6 size-12 rounded-full bg-bg-success-secondary p-3 text-fg-success-primary" />
		)}
		{!customIcon && variant === "error" && (
			<AlertCircle className="absolute top-6 left-6 size-12 rounded-full bg-bg-error-secondary p-3 text-fg-error-primary" />
		)}
		{!customIcon && variant === "warning" && (
			<AlertCircle className="absolute top-6 left-6 size-12 rounded-full bg-bg-warning-secondary p-3 text-fg-warning-primary" />
		)}
		{!customIcon && variant === "info" && (
			<InfoCircle className="absolute top-6 left-6 size-12 rounded-full bg-bg-info-secondary p-3 text-fg-info-primary" />
		)}
	</div>
);

type StatusModalAction = {
	label: string;
	onClick: () => void;
	color?: "primary" | "secondary";
	isDisabled?: boolean;
	isLoading?: boolean;
	/** Mantém o rótulo visível junto ao spinner (ex.: “Usar checklist”) */
	showTextWhileLoading?: boolean;
	iconLeading?: ReactNode;
};

type StatusModalProps = {
	isOpen: boolean;
	title: string;
	description: string;
	onClose: () => void;
	actions: StatusModalAction[];
	variant?: "success" | "error" | "warning" | "info";
	content?: ReactNode;
	icon?: ReactNode;
};

export const StatusModal = ({
	actions,
	content,
	description,
	icon,
	isOpen,
	onClose,
	title,
	variant = "success",
}: StatusModalProps) => (
	<ModalOverlay
		className="items-center"
		isOpen={isOpen}
		onOpenChange={(open) => {
			if (!open) {
				onClose();
			}
		}}
	>
		<Modal className="relative mx-auto h-fit w-full max-w-[440px] overflow-hidden rounded-2xl bg-bg-primary">
			<ModalBackgroundCircle customIcon={icon} variant={variant} />
			<Button
				aria-label="Fechar modal"
				className="absolute top-3 right-3 z-10 size-11 p-2 text-fg-secondary"
				color="link-gray"
				onClick={onClose}
				type="button"
			>
				<XClose aria-hidden="true" className="size-6 text-fg-quaternary" />
			</Button>
			<DialogUntitledUI
				aria-label="Modal de status"
				className="relative z-auto mt-[88px] flex max-w-[440px] flex-col items-start p-6 pt-0 shadow-lg"
				role="alertdialog"
			>
				<div className="">
					<h4 className="mb-0.5 font-semibold text-fg-primary text-md">
						{title}
					</h4>
					<p className="text-fg-secondary text-sm">{description}</p>
				</div>
				{content ? content : null}
				<div className="mt-8 flex w-full flex-wrap gap-3">
					{actions.map((action) => (
						<Button
							aria-label={action.label}
							className="ml-auto"
							color={action.color === "secondary" ? "secondary" : "primary"}
							iconLeading={action.iconLeading}
							isDisabled={action.isDisabled}
							isLoading={action.isLoading}
							key={action.label}
							onClick={action.onClick}
							showTextWhileLoading={action.showTextWhileLoading}
							size="lg"
						>
							{action.label}
						</Button>
					))}
				</div>
			</DialogUntitledUI>
		</Modal>
	</ModalOverlay>
);
